import { EventEmitter, Injectable, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, DialogData } from '../components/dialog/confirmation-dialog.component';
import { POPUP_TEXT } from 'src/app/shared/text/popup-texts';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Filesystem, Directory, ProgressStatus } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { UtilityFunctionsService } from './utility-functions.service';

// Constants for user cancellation detection
const CANCELLATION_TERMS = [
  'aborted',
  'canceled',
  'cancelled',
  'aborted by user',
  'user cancelled',
  'user canceled',
  'operation cancelled',
  'operation canceled',
  'The user cancelled the operation',
  'cancelled by user',
  'canceled by user',
];

export interface DownloadState {
  isDownloading: boolean;
  modelId: string | null;
  modelName?: string;
  progress: number;
  abortController?: AbortController;
  listener?: any; // Filesystem listener
  downloadCancelled?: boolean; // Add flag to track if download was cancelled
}

export interface DownloadOptions {
  id: string;
  modelName?: string;
  url: string;
  fileName: string;
  onProgress?: (progress: number) => void;
}

export interface DownloadResult {
  success: boolean;
  wasCancelled: boolean;
  filePath?: string;
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class MobileHelperService {
  @Output() public mobileAuthPopupClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

  private downloadStateSubject = new BehaviorSubject<DownloadState>({
    isDownloading: false,
    modelId: null,
    progress: 0
  });

  public downloadState$ = this.downloadStateSubject.asObservable();
  private progressSubject = new Subject<number>();
  public progress$ = this.progressSubject.asObservable();

  constructor(private dialog: MatDialog, private utilityService: UtilityFunctionsService) { }

  updateDownloadState(state: DownloadState): void {
    this.downloadStateSubject.next(state);
  }

  getCurrentDownloadState(): DownloadState {
    return this.downloadStateSubject.getValue();
  }

  isDownloadInProgress(): boolean {
    return this.downloadStateSubject.getValue().isDownloading;
  }

  getActiveDownload(): { id: string; progress: number } | null {
    const currentState = this.getCurrentDownloadState();
    if (!currentState.isDownloading || !currentState.modelId) {
      return null;
    }

    return {
      id: currentState.modelId,
      progress: currentState.progress
    };
  }

  async resetDownloadState(): Promise<void> {
    const currentState = this.getCurrentDownloadState();

    // Mark the download as cancelled
    this.updateDownloadState({
      ...currentState,
      downloadCancelled: true
    });

    // Cancel download if AbortController exists
    if (currentState.abortController) {
      try {
        currentState.abortController.abort();
        console.log('Download aborted by controller');
      } catch (error) {
        console.error('Error aborting download:', error);
      }
    }

    // Remove progress listener if it exists
    if (currentState.listener) {
      try {
        await currentState.listener.remove();
        console.log('Progress listener removed');
      } catch (error) {
        console.error('Error removing progress listener:', error);
      }
    }

    // Reset state
    this.updateDownloadState({
      isDownloading: false,
      modelId: null,
      progress: 0,
      downloadCancelled: true // Keep this flag to indicate cancellation
    });
  }

  /**
   * Perform file download with progress tracking
   * @param options Download options
   * @returns Observable that emits download progress
   */
  async downloadFile(options: DownloadOptions): Promise<DownloadResult> {
    const { id, modelName, url, fileName, onProgress } = options;

    try {
      // Create AbortController for download cancellation
      const abortController = new AbortController();

      // Setup progress listener
      const listener = await Filesystem.addListener(
        'progress',
        (p: ProgressStatus) => {
          if (p.url !== url) return;
          const progress = Math.round((p.bytes / p.contentLength) * 100);
          this.progressSubject.next(progress);

          // Also update the download state
          const state = this.getCurrentDownloadState();
          this.updateDownloadState({
            ...state,
            progress
          });

          // Call onProgress callback if provided
          if (onProgress) {
            onProgress(progress);
          }
        },
      );

      // Update download state
      this.updateDownloadState({
        isDownloading: true,
        modelId: id,
        modelName: modelName || fileName,
        progress: 0,
        abortController,
        listener,
        downloadCancelled: false
      });

      // Perform download
      const { path } = await Filesystem.downloadFile({
        url,
        directory: Directory.Cache,
        path: fileName,
        progress: true,
        recursive: true,
      });

      // Check if download was cancelled during the process
      const currentState = this.getCurrentDownloadState();
      if (currentState.downloadCancelled) {
        console.log('Download was cancelled, cleaning up...');

        // Remove the downloaded file if it exists
        try {
          await Filesystem.deleteFile({
            path: fileName,
            directory: Directory.Cache
          });
          console.log('Deleted cancelled download file');
        } catch (deleteErr) {
          console.log('Could not delete file or file doesn\'t exist', deleteErr);
        }

        // Remove the progress listener
        await listener.remove();

        return {
          success: false,
          wasCancelled: true
        };
      }

      // Download completed successfully
      // Remove the progress listener
      await listener.remove();

      // Return successful result with file path
      return {
        success: true,
        wasCancelled: false,
        filePath: path
      };

    } catch (err) {
      // Check if download was explicitly cancelled via our interface
      const currentState = this.getCurrentDownloadState();
      if (currentState.downloadCancelled) {
        return {
          success: false,
          wasCancelled: true
        };
      }

      // Check if error was caused by user cancellation
      const errorMsg = err.message?.toLowerCase() || '';
      const wasCancelled = CANCELLATION_TERMS.some(term =>
        errorMsg.includes(term.toLowerCase())
      );

      if (wasCancelled) {
        console.log('Download operation cancelled by user');
        return {
          success: false,
          wasCancelled: true,
          error: err
        };
      } else {
        console.error('[Download error]', err);
        return {
          success: false,
          wasCancelled: false,
          error: err
        };
      }
    }
  }

  /**
   * Share a file with the Share API
   * @param filePath Path to the file to share
   * @param title Title for the share dialog
   * @returns Whether the share was successful or cancelled
   */
  async shareFile(filePath: string, title: string = 'Save model file'): Promise<{ success: boolean; wasCancelled: boolean; error?: any }> {
    try {
      // Check if download was cancelled before sharing
      const currentState = this.getCurrentDownloadState();
      if (currentState.downloadCancelled) {
        console.log('Sharing cancelled because download was cancelled');
        return { success: false, wasCancelled: true };
      }

      await Share.share({
        title,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        files: [filePath],
        dialogTitle: title,
      });

      return { success: true, wasCancelled: false };
    } catch (err) {
      // Check if error was caused by user cancellation
      const errorMsg = err.message?.toLowerCase() || '';
      const wasCancelled = CANCELLATION_TERMS.some(term =>
        errorMsg.includes(term.toLowerCase())
      );

      if (wasCancelled) {
        console.log('Share operation cancelled by user');
        return { success: false, wasCancelled: true };
      } else {
        console.error('[Share error]', err);
        return { success: false, wasCancelled: false, error: err };
      }
    }
  }

  showDownloadSwitchConfirmation(currentModelName: string = 'current model', newModelName: string = 'new model'): Observable<boolean> {
    const dialogData: DialogData = this.utilityService.applyPopupText(POPUP_TEXT.DOWNLOAD_SWITCH, {
      '{currentModelName}': currentModelName,
      '{newModelName}': newModelName
    });

    return this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
      width: '80%',
      maxWidth: '400px'
    }).afterClosed();
  }

  showCancelDownloadConfirmation(modelName: string = 'current model', action: string = 'continue'): Observable<boolean> {
    const dialogData: DialogData = this.utilityService.applyPopupText(POPUP_TEXT.DOWNLOAD_CANCEL, {
      '{modelName}': modelName,
      '{action}': action
    });

    return this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
      width: '80%',
      maxWidth: '400px'
    }).afterClosed();
  }
}
